import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EgressView, PlatformSettingView } from "@shared/types/settings";
import { apiRequest } from "@/lib/api";

/**
 * Deployment-wide settings (FR-72) and the computed egress allow-list.
 *
 * The catalog is closed server-side, so the screen renders whatever
 * `GET /platform/settings` returns rather than a hard-coded list of cards: a
 * setting added to `PLATFORM_SETTINGS` appears here without a client change,
 * and one removed stops appearing instead of writing to a key that no longer
 * validates.
 */
export const PLATFORM_SETTINGS_PATH = "/api/v2/platform/settings";
export const PLATFORM_EGRESS_PATH = "/api/v2/platform/settings/egress";

export const PLATFORM_SETTINGS_QUERY_KEY = [PLATFORM_SETTINGS_PATH] as const;
export const PLATFORM_EGRESS_QUERY_KEY = [PLATFORM_EGRESS_PATH] as const;

export function usePlatformSettings() {
  return useQuery<PlatformSettingView[]>({ queryKey: PLATFORM_SETTINGS_QUERY_KEY });
}

export function useEgressView() {
  return useQuery<EgressView>({ queryKey: PLATFORM_EGRESS_QUERY_KEY });
}

/**
 * Write one setting.
 *
 * `value` and `secretValue` are mutually exclusive and the route says so; a
 * secret travels once, on the way in, and the response carries the vault
 * pointer rather than the value (SEC-2).
 */
export function useUpdatePlatformSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      value,
      secretValue,
    }: {
      key: string;
      value?: unknown;
      secretValue?: string;
    }) =>
      apiRequest<PlatformSettingView>(`${PLATFORM_SETTINGS_PATH}/${key}`, {
        method: "PUT",
        body: secretValue === undefined ? { value } : { secretValue },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PLATFORM_SETTINGS_QUERY_KEY }),
  });
}

/** The view for one key, or undefined when the deployment has never written it. */
export function settingByKey(
  settings: PlatformSettingView[] | undefined,
  key: string,
): PlatformSettingView | undefined {
  return settings?.find((setting) => setting.key === key);
}
